import { t } from '@/lib/i18n';
import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/format';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700',
  secondary: 'surface-card text-body border line hover:surface-hover hover:text-strong',
  ghost: 'text-muted hover:text-strong hover:bg-[var(--surface-hover)]',
  danger: 'bg-red-600 text-white hover:bg-red-700',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  loading?: boolean;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', loading, disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex h-9 items-center justify-center gap-2 rounded-lg px-3.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-55',
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-5 animate-spin text-muted', className)} role="status" aria-label={t('ui.loading')} />;
}
export function CenteredSpinner({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16">
      <Spinner />
      {label && <p className="text-sm text-muted">{label}</p>}
    </div>
  );
}
export function EmptyState({ icon, title, description, action }: { icon?: ReactNode; title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      {icon && <div className="text-faint [&>svg]:size-8" aria-hidden>{icon}</div>}
      <h3 className="text-sm font-semibold text-strong">{title}</h3>
      {description && <p className="max-w-sm text-sm text-muted">{description}</p>}
      {action}
    </div>
  );
}
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : t('ui.unknownError');
  return (
    <div className="m-4 rounded-[var(--radius-card)] border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/30" role="alert">
      <p className="text-sm font-semibold text-red-800 dark:text-red-300">{t('ui.somethingWentWrong')}</p>
      <p className="mt-1 text-sm text-red-700 dark:text-red-400">{msg}</p>
      {onRetry && <Button variant="secondary" className="mt-3 h-8" onClick={onRetry}>{t('ui.tryAgain')}</Button>}
    </div>
  );
}

/**
 * `selectable` opts a chip into being copyable text: it gets a text cursor and
 * an outline so it still reads as a pill against a row that is otherwise
 * clickable. Chips inside a button are not selectable — the drag becomes a
 * click — so callers that want copyable codes must render them outside one.
 */
export function Chip({
  children,
  tone = 'default',
  selectable = false,
  size = 'sm',
}: {
  children: ReactNode;
  tone?: 'default' | 'brand' | 'amber';
  selectable?: boolean;
  /**
   * `md` is for chips that carry a header rather than annotate a dense list.
   * Opt-in rather than a new default: the artwork, basket and activity pages
   * pack these into tight rows where 11px is the point.
   */
  size?: 'sm' | 'md';
}) {
  const tones = {
    default: 'surface-sunken text-muted',
    brand: 'bg-brand-50 text-brand-800 dark:bg-brand-900/40 dark:text-brand-100',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-[11px]',
    md: 'px-2.5 py-1 text-xs',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium',
        sizes[size],
        tones[tone],
        selectable && 'cursor-text select-text ring-1 ring-inset ring-black/10 dark:ring-white/15',
      )}
    >
      {children}
    </span>
  );
}
